import React, { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import api from "../services/api";
import { ArrowLeft } from "lucide-react";

const useQuery = () => new URLSearchParams(useLocation().search);

const VerifyEmail: React.FC = () => {
  const history = useHistory();
  const query = useQuery();
  const { toast } = useToast();
  const verificationSentRef = useRef(false);

  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const token = query.get("token");
    if (!token || verificationSentRef.current) return;

    verificationSentRef.current = true;
    const verify = async () => {
      try {
        await api.get(`/verify-email?token=${encodeURIComponent(token)}`);
        setStatus("ok");
      } catch {
        setStatus("error");
      }
    };

    verify();
  }, [query]);

  const resendEmail = async () => {
    if (!email) {
      toast({
        title: "Errore",
        description: "Inserisci la tua email per reinviare la verifica.",
        variant: "error",
      });
      return;
    }

    try {
      await api.post("/resend-verification", { email });
      toast({
        title: "Email inviata",
        description:
          "Se l'email è corretta, riceverai le istruzioni per la verifica. Controlla anche la tua cartella spam.",
      });
    } catch {
      toast({
        title: "Errore",
        description: "Errore nell'invio della mail di verifica.",
        variant: "error",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="glass-card w-full max-w-md rounded-2xl p-8 space-y-6 text-center relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 rounded-full"
          onClick={() => history.push("/")}
        >
          <ArrowLeft className="size-6 text-white" />
        </Button>

        <img
          src="./assets/long_light.png"
          alt="logo"
          className="h-16 mx-auto mb-4"
        />

        {status === "idle" && (
          <>
            <p className="text-white/90">
              Controlla la tua email per il link di verifica.
            </p>

            <div className="space-y-4 mt-6 text-left">
              <Label htmlFor="email" className="text-white/80">
                Non l'hai ricevuta? Reinvia:
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Inserisci la tua email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl text-white"
              />

              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-teal-200 to-teal-500 text-white hover:opacity-90"
                onClick={resendEmail}
              >
                Reinvia
              </Button>
            </div>
          </>
        )}

        {status === "ok" && (
          <>
            <p className="text-teal-400 font-medium">
              Email verificata! Ora puoi accedere.
            </p>
            <Button
              size="lg"
              className="w-full bg-gradient-to-r from-teal-200 to-teal-500 text-white hover:opacity-90 mt-4"
              onClick={() => history.replace("/")}
            >
              Vai al login
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <p className="text-red-400 font-medium">
              Link non valido o scaduto.
            </p>
            <Button
              size="lg"
              className="w-full bg-red-600 text-white hover:bg-red-700 mt-4"
              onClick={() => history.replace("/")}
            >
              Torna al login
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
